'use client';

import { SessionProvider, useSession } from 'next-auth/react';
import { PropsWithChildren, Suspense, useEffect } from 'react';
import { SWRConfig, SWRConfiguration } from 'swr';

import { CurrentPageInfoProvider } from '../analytics/CurrentPageInfoProvider';
import { AppInsightsPluginProvider } from '../analytics/appInsights/AppInsightsPluginProvider';
import { AppInsightsProvider } from '../analytics/appInsights/AppInsightsProvider';
import { setSessionCache } from '../api/sessionCache';
import { LocalizationProvider } from '../i18n/LocalizationProvider';
import { TranslationsProvider } from '../i18n/TranslationsProvider';
import { ProfileProvider } from './ProfileProvider';
import { SiteSettingsProvider } from './SiteSettingsProvider';
import { StoreConfigProvider } from './StoreConfigProvider';

// By default all SWR hooks don't revalidate data
const SWR_CONFIG: SWRConfiguration = {
	revalidateIfStale: false,
	revalidateOnFocus: false,
	revalidateOnReconnect: false,
};

/**
 * Syncs the session from SessionProvider into a module-level cache
 * so non-React code (axios interceptors) can access it without
 * triggering additional /api/auth/session requests.
 */
const SessionCacheSync = () => {
	const { data: session } = useSession();

	useEffect(() => {
		setSessionCache(session ?? null);
	}, [session]);

	return null;
};

/**
 * This is a common wrapper used by multiple routes such `[lang]/[[...slug]]`, `auth`, etc.
 * It's not included in `./layout.tsx` because:
 *  1. `CommonProviders` needs to be a client component
 *  2. Some routes don't need these providers
 *
 * TODO: update as many providers as possible to be server components
 */
export const CommonProviders = ({ children }: PropsWithChildren) => {
	return (
		<Suspense>
			<SWRConfig value={SWR_CONFIG}>
				<SessionProvider refetchOnWindowFocus={false}>
					<SessionCacheSync />
					<ProfileProvider>
						<CurrentPageInfoProvider>
							<AppInsightsPluginProvider>
								<AppInsightsProvider>
									<TranslationsProvider>
										<SiteSettingsProvider>
											<StoreConfigProvider>
												<LocalizationProvider>{children}</LocalizationProvider>
											</StoreConfigProvider>
										</SiteSettingsProvider>
									</TranslationsProvider>
								</AppInsightsProvider>
							</AppInsightsPluginProvider>
						</CurrentPageInfoProvider>
					</ProfileProvider>
				</SessionProvider>
			</SWRConfig>
		</Suspense>
	);
};
